import type { ReactNode } from 'react';

/**
 * The boxed sentence the admin screens use to say « read this before you click »
 * (maquette `.callout`, BUILD-856).
 *
 * ## Why it is one component and not twelve spellings
 *
 * Before this file the same object existed seven different ways across the
 * wizard alone - `border-2 border-bleu-nuit p-4`, `border border-jaune
 * bg-jaune-tint p-3`, `bg-bleu-ciel-tint px-3 py-2`, `border border-rouge
 * bg-rouge-tint p-3` - so the SAME kind of warning looked like four different
 * kinds of thing depending on which step you were standing on. That is not a
 * cosmetic complaint: the operator learns what a box means from its shape, and a
 * shape that changes per screen teaches nothing. The maquette draws exactly one
 * callout and colours it by TONE.
 *
 * ## The tone is a meaning, never a decoration
 *
 * The charte holds this app at level 2 - black, white and `bleu-nuit` - and
 * authorises a noyau only where it carries a meaning. The four tones here are
 * the four the charte names (§03): `info` = bleu-ciel, `success` = vert, `warn`
 * = jaune (text at `-deep`, white on jaune being banned), `danger` = rouge.
 * `neutral` is the fifth and it is the DEFAULT: a box that has nothing to signal
 * stays grey rather than borrowing a colour for emphasis.
 *
 * The left rule is what makes the tone legible at a glance without flooding the
 * page: a 4px edge plus a tint, never a filled block.
 */

export type CalloutTone = 'neutral' | 'info' | 'success' | 'warn' | 'danger';

/**
 * The ground and the rule, per tone.
 *
 * A `Record` over the union: a tone added without its two classes stops
 * compiling, which is the régime every label table of this codebase follows.
 */
const TONE_CLASS: Readonly<Record<CalloutTone, string>> = {
  neutral: 'border-l-gray-400 bg-white',
  info: 'border-l-bleu-ciel bg-bleu-ciel-tint',
  success: 'border-l-vert bg-vert-tint',
  warn: 'border-l-jaune-deep bg-jaune-tint',
  danger: 'border-l-rouge bg-rouge-tint',
};

export interface CalloutProps {
  tone?: CalloutTone;
  /** An optional bold first line. Most callouts are one sentence and need none. */
  title?: string;
  children: ReactNode;
  /**
   * Announce this box when it appears, and how loudly.
   *
   * `alert` interrupts - a refusal, a failed write. `status` is polite: it
   * waits for a pause, which is what a box carrying several kinds of news
   * wants. Left off for the standing warnings, which are part of the page
   * rather than an event.
   *
   * NAMED `live` and not `role`, which is a lint trap and not a taste (#35).
   * Biome's `lint/a11y/useSemanticElements` reads the JSX attribute `role` on
   * any element, custom components included, and refuses `role="status"` by
   * naming the element it wants instead. So `<Callout role="status">` could
   * never compile clean at a CALL SITE, whatever this component did with it -
   * the prop was unusable from the day it was written, and no caller had ever
   * passed it. `live` says what the caller means; the ARIA is this file's job.
   */
  live?: 'alert' | 'status';
  className?: string;
  'data-testid'?: string;
}

/**
 * A polite live region is an `<output>`, and that is not a preference (#35).
 *
 * The ELEMENT follows the announcement rather than a role being bolted onto a
 * fixed `<div>`: `<output>` carries `role="status"` implicitly, which is why
 * none is spelled out on it - and why the one polite region this codebase
 * already had (`components/ui/pms-spinner.tsx`) is a bare `<output>` too.
 * `alert` has no element of its own and stays an attribute.
 *
 * The alternative was a second spelling of this box beside the door's form,
 * which is the thing this file's header spends twenty lines arguing against.
 */
export function Callout({
  tone = 'neutral',
  title,
  children,
  live,
  className,
  'data-testid': testId,
}: CalloutProps) {
  /*
    NO `display` in this shared string, and that is a rule of this codebase
    rather than an oversight: `components/admin/admin-rail.tsx` states it three
    times, from a real defect. The caller's `className` is concatenated after
    this one into a single `class` attribute, so two utilities of equal
    specificity would be decided by Tailwind's stylesheet order and not by
    intent - the first caller writing `className="flex …"` or
    `className="hidden md:block"` would get whichever the sheet happens to emit
    last. The `<output>` branch below sets its own `block`, because it is the
    only element here that needs one.
  */
  const shell = `rounded-md border border-gray-200 border-l-4 px-3.5 py-2.5 text-gray-800 text-sm ${TONE_CLASS[tone]} ${className ?? ''}`;
  /*
    A `<span className="block">` and not a `<p>`: the `<output>` branch below
    accepts PHRASING content only, and a `<p>` inside it is invalid HTML that
    the parser would hoist out of the box. One spelling for both branches, so
    the pair `live="status"` + `title` cannot quietly produce a broken tree.
  */
  const inside = (
    <>
      {title === undefined ? null : (
        <span className="block font-semibold text-gray-900 text-sm">{title}</span>
      )}
      {children}
    </>
  );

  if (live === 'status') {
    return (
      <output data-testid={testId} data-tone={tone} className={`block ${shell}`}>
        {inside}
      </output>
    );
  }

  return (
    <div
      {...(live === undefined ? {} : { role: live })}
      data-testid={testId}
      data-tone={tone}
      className={shell}
    >
      {inside}
    </div>
  );
}
